import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/apiService';
import API_CONFIG from '../config/api';
import Modal from './Modal';
import FormModal from './ui/FormModal';
import ActionMenu from './ui/ActionMenu';
import { PageHeader, PageShell, Panel } from './ui/PageShell';
import { getMenuIcon, getMenuLabel } from '../utils/menuLabels';
import { ui } from './ui/styles';
import './Usuarios.css';

const Usuarios = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    email: '',
    active: true
  });
  const [formUserRoles, setFormUserRoles] = useState([]);
  const [editingUserId, setEditingUserId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, userId: null });

  // Carregar usuários e menus
  useEffect(() => {
    loadUsers();
    loadMenus();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await apiService.authenticatedRequest(
        API_CONFIG.ENDPOINTS.USERS.LIST,
        {
          method: 'GET'
        }
      );

      if (response.success) {
        setUsers(response.data || []);
      } else {
        toast.error(response.error || 'Erro ao carregar usuários');
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const loadMenus = async () => {
    try {
      const response = await apiService.authenticatedRequest(
        API_CONFIG.ENDPOINTS.USERS.MENUS,
        {
          method: 'GET'
        }
      );

      if (response.success) {
        setMenus(response.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar menus:', error);
    }
  };

  const loadUserRoles = async (userId) => {
    try {
      const response = await apiService.authenticatedRequest(
        `${API_CONFIG.ENDPOINTS.USERS.GET}/${userId}/roles`,
        {
          method: 'GET'
        }
      );

      if (response.success) {
        const roles = response.data || [];
        setUserRoles(roles);
        return roles; // Retornar para usar diretamente
      }
      return [];
    } catch (error) {
      console.error('Erro ao carregar roles do usuário:', error);
      toast.error('Erro ao carregar permissões do usuário');
      return [];
    }
  };

  const handleOpenModal = async (user = null) => {
    if (user) {
      setEditingUserId(user.id);
      setFormData({
        username: user.username,
        password: '',
        name: user.name || '',
        email: user.email || '',
        active: user.active !== undefined ? user.active : true
      });
      // Carregar roles do usuário
      const roles = await loadUserRoles(user.id);
      setFormUserRoles(roles || []);
    } else {
      setEditingUserId(null);
      setFormData({
        username: '',
        password: '',
        name: '',
        email: '',
        active: true
      });
      // Limpar roles
      setFormUserRoles([]);
    }
    setShowModal(true);
  };

  const handleOpenRolesModal = async (user) => {
    setSelectedUser(user);
    await loadUserRoles(user.id);
    setShowRolesModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUserId(null);
    setFormData({
      username: '',
      password: '',
      name: '',
      email: '',
      active: true
    });
    setFormUserRoles([]);
  };

  const handleCloseRolesModal = () => {
    setShowRolesModal(false);
    setSelectedUser(null);
    setUserRoles([]);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const url = editingUserId
        ? `${API_CONFIG.ENDPOINTS.USERS.UPDATE}/${editingUserId}`
        : API_CONFIG.ENDPOINTS.USERS.CREATE;

      const method = editingUserId ? 'PUT' : 'POST';
      const dataToSend = { ...formData };
      
      // Se estiver editando e não forneceu senha, remover do objeto
      if (editingUserId && !dataToSend.password) {
        delete dataToSend.password;
      }

      // Definir role padrão como 'user' (ainda necessário para o backend)
      dataToSend.role = 'user';

      const response = await apiService.authenticatedRequest(
        url,
        {
          method,
          body: JSON.stringify(dataToSend)
        }
      );

      if (response.success) {
        const userId = editingUserId || response.data.id;
        
        // Salvar roles do usuário
        if (formUserRoles.length > 0) {
          const rolesToSave = formUserRoles.map(role => ({
            menu_id: role.menu_id,
            can_view: role.can_view || false,
            can_create: role.can_create || false,
            can_edit: role.can_edit || false,
            can_delete: role.can_delete || false
          }));

          await apiService.authenticatedRequest(
            `${API_CONFIG.ENDPOINTS.USERS.GET}/${userId}/roles`,
            {
              method: 'PUT',
              body: JSON.stringify({ roles: rolesToSave })
            }
          );
        }

        toast.success(editingUserId ? 'Usuário atualizado com sucesso!' : 'Usuário criado com sucesso!');
        handleCloseModal();
        loadUsers();
      } else {
        toast.error(response.error || 'Erro ao salvar usuário');
      }
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      toast.error('Erro ao salvar usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    setDeleteModal({ isOpen: true, userId });
  };

  const confirmDelete = async () => {
    const userId = deleteModal.userId;
    if (!userId) return;

    try {
      setLoading(true);
      const response = await apiService.authenticatedRequest(
        `${API_CONFIG.ENDPOINTS.USERS.DELETE}/${userId}`,
        {
          method: 'DELETE'
        }
      );

      if (response.success) {
        toast.success('Usuário deletado com sucesso!');
        loadUsers();
      } else {
        toast.error(response.error || 'Erro ao deletar usuário');
      }
    } catch (error) {
      console.error('Erro ao deletar usuário:', error);
      toast.error('Erro ao deletar usuário');
    } finally {
      setLoading(false);
      setDeleteModal({ isOpen: false, userId: null });
    }
  };

  const handleRoleChange = (menuId, permission, value) => {
    setUserRoles(prev => {
      const existing = prev.find(r => r.menu_id === menuId);
      if (existing) {
        return prev.map(r =>
          r.menu_id === menuId ? { ...r, [permission]: value } : r
        );
      } else {
        return [...prev, {
          menu_id: menuId,
          can_view: permission === 'can_view' ? value : false,
          can_create: permission === 'can_create' ? value : false,
          can_edit: permission === 'can_edit' ? value : false,
          can_delete: permission === 'can_delete' ? value : false
        }];
      }
    });
  };

  const handleFormRoleChange = (menuId, permission, value) => {
    setFormUserRoles(prev => {
      const existing = prev.find(r => r.menu_id === menuId);
      if (existing) {
        return prev.map(r =>
          r.menu_id === menuId ? { ...r, [permission]: value } : r
        );
      } else {
        return [...prev, {
          menu_id: menuId,
          can_view: permission === 'can_view' ? value : false,
          can_create: permission === 'can_create' ? value : false,
          can_edit: permission === 'can_edit' ? value : false,
          can_delete: permission === 'can_delete' ? value : false
        }];
      }
    });
  };

  // Obter role do formulário para um menu
  const getFormRoleForMenu = (menuId) => {
    return formUserRoles.find(r => r.menu_id === menuId) || {
      menu_id: menuId,
      can_view: false,
      can_create: false,
      can_edit: false,
      can_delete: false
    };
  };

  // Marcar/desmarcar todas as permissões de um menu
  const handleSelectAllMenu = (menuId, checked) => {
    setFormUserRoles(prev => {
      const existing = prev.find(r => r.menu_id === menuId);
      if (existing) {
        return prev.map(r =>
          r.menu_id === menuId
            ? {
                ...r,
                can_view: checked,
                can_create: checked,
                can_edit: checked,
                can_delete: checked
              }
            : r
        );
      } else {
        return [...prev, {
          menu_id: menuId,
          can_view: checked,
          can_create: checked,
          can_edit: checked,
          can_delete: checked
        }];
      }
    });
  };

  // Verificar se todas as permissões de um menu estão marcadas
  const isAllMenuSelected = (menuId) => {
    const role = getFormRoleForMenu(menuId);
    return role.can_view && role.can_create && role.can_edit && role.can_delete;
  };

  // Verificar se algumas (mas não todas) permissões estão marcadas
  const isSomeMenuSelected = (menuId) => {
    const role = getFormRoleForMenu(menuId);
    return (role.can_view || role.can_create || role.can_edit || role.can_delete) && 
           !(role.can_view && role.can_create && role.can_edit && role.can_delete);
  };

  // Marcar/desmarcar todas as permissões de todos os menus
  const handleSelectAllMenus = (checked) => {
    const allMenuIds = [];
    organizedMenus.forEach(menu => {
      allMenuIds.push(menu.id);
      if (menu.children && menu.children.length > 0) {
        menu.children.forEach(child => allMenuIds.push(child.id));
      }
    });

    const newRoles = allMenuIds.map(menuId => ({
      menu_id: menuId,
      can_view: checked,
      can_create: checked,
      can_edit: checked,
      can_delete: checked
    }));

    setFormUserRoles(newRoles);
  };

  // Verificar se todas as permissões de todos os menus estão marcadas
  const isAllMenusSelected = () => {
    let allSelected = true;
    organizedMenus.forEach(menu => {
      if (!isAllMenuSelected(menu.id)) {
        allSelected = false;
      }
      if (menu.children && menu.children.length > 0) {
        menu.children.forEach(child => {
          if (!isAllMenuSelected(child.id)) {
            allSelected = false;
          }
        });
      }
    });
    return allSelected;
  };

  // Verificar se algumas permissões estão marcadas
  const isSomeMenusSelected = () => {
    let someSelected = false;
    organizedMenus.forEach(menu => {
      if (isSomeMenuSelected(menu.id) || isAllMenuSelected(menu.id)) {
        someSelected = true;
      }
      if (menu.children && menu.children.length > 0) {
        menu.children.forEach(child => {
          if (isSomeMenuSelected(child.id) || isAllMenuSelected(child.id)) {
            someSelected = true;
          }
        });
      }
    });
    return someSelected && !isAllMenusSelected();
  };

  const handleSaveRoles = async () => {
    try {
      setLoading(true);
      const rolesToSave = userRoles.map(role => ({
        menu_id: role.menu_id,
        can_view: role.can_view || false,
        can_create: role.can_create || false,
        can_edit: role.can_edit || false,
        can_delete: role.can_delete || false
      }));

      const response = await apiService.authenticatedRequest(
        `${API_CONFIG.ENDPOINTS.USERS.GET}/${selectedUser.id}/roles`,
        {
          method: 'PUT',
          body: JSON.stringify({ roles: rolesToSave })
        }
      );

      if (response.success) {
        toast.success('Permissões atualizadas com sucesso!');
        handleCloseRolesModal();
        loadUsers();
      } else {
        toast.error(response.error || 'Erro ao salvar permissões');
      }
    } catch (error) {
      console.error('Erro ao salvar permissões:', error);
      toast.error('Erro ao salvar permissões');
    } finally {
      setLoading(false);
    }
  };

  // Organizar menus por hierarquia (pais e filhos)
  const organizedMenus = menus.reduce((acc, menu) => {
    if (!menu.parent_id) {
      // Menu pai
      acc.push({
        ...menu,
        children: menus.filter(m => m.parent_id === menu.id)
      });
    }
    return acc;
  }, []);

  // Obter role do usuário para um menu
  const getUserRoleForMenu = (menuId) => {
    return userRoles.find(r => r.menu_id === menuId) || {
      menu_id: menuId,
      can_view: false,
      can_create: false,
      can_edit: false,
      can_delete: false
    };
  };

  const renderMenuTitle = (menu) => {
    const Icon = getMenuIcon(menu.name);
    return (
      <span className="inline-flex items-center gap-2">
        <Icon size={16} className="text-primary" />
        {getMenuLabel(menu)}
      </span>
    );
  };

  return (
    <PageShell>
      <PageHeader
        title="Gerenciamento de Usuários"
        description="Cadastre usuários e defina permissões de acesso aos módulos."
        actions={(
          <button type="button" className={ui.btnPrimary} onClick={() => handleOpenModal()}>
            <Plus size={16} />
            Novo Usuário
          </button>
        )}
      />

      <div className={ui.pageBody}>
        {loading && !users.length && (
          <div className="loading">Carregando usuários...</div>
        )}

        <Panel className="overflow-visible">
          <div className="usuarios-table-container overflow-visible border-0 shadow-none">
            <table className="usuarios-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nome de Usuário</th>
              <th>Nome</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td>{user.id}</td>
                <td>{user.username}</td>
                <td>{user.name}</td>
                <td>{user.email || '-'}</td>
                <td>
                  <span className={`badge badge-${user.role}`}>
                    {user.role === 'admin' ? 'Administrador' : 'Usuário'}
                  </span>
                </td>
                <td>
                  <span className={`badge badge-${user.active ? 'active' : 'inactive'}`}>
                    {user.active ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td>
                  <ActionMenu
                    onEdit={() => handleOpenModal(user)}
                    onRoles={() => handleOpenRolesModal(user)}
                    onDelete={currentUser?.id !== user.id ? () => handleDelete(user.id) : undefined}
                  />
                </td>
              </tr>
            ))}
          </tbody>
            </table>
          </div>
        </Panel>
      </div>

      <FormModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingUserId ? 'Editar Usuário' : 'Novo Usuário'}
        size="xl"
        footer={(
          <>
            <button type="button" className={ui.btn} onClick={handleCloseModal}>
              Cancelar
            </button>
            <button type="submit" form="usuario-form" className={ui.btnPrimary} disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar'}
            </button>
          </>
        )}
      >
        <form id="usuario-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className={ui.label}>Nome de Usuário *</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className={ui.input}
                required
                disabled={!!editingUserId}
              />
            </div>
            <div>
              <label className={ui.label}>
                {editingUserId ? 'Nova Senha (deixe em branco para manter)' : 'Senha *'}
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={ui.input}
                required={!editingUserId}
                minLength={6}
              />
            </div>
            <div>
              <label className={ui.label}>Nome Completo *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={ui.input}
                required
              />
            </div>
            <div>
              <label className={ui.label}>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={ui.input}
              />
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-text">
            <input
              type="checkbox"
              name="active"
              checked={formData.active}
              onChange={handleInputChange}
              className="rounded border-border text-primary focus:ring-primary/20"
            />
            Usuário Ativo
          </label>

          <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <label className="text-sm font-medium text-text">Permissões de Acesso aos Menus</label>
              <label className="inline-flex items-center gap-2 text-sm text-muted">
                <input
                  type="checkbox"
                  checked={isAllMenusSelected()}
                  ref={(input) => {
                    if (input) input.indeterminate = isSomeMenusSelected();
                  }}
                  onChange={(e) => handleSelectAllMenus(e.target.checked)}
                  className="rounded border-border text-primary focus:ring-primary/20"
                />
                Marcar Todas
              </label>
            </div>
            <div className="form-roles-container max-h-80 space-y-3 overflow-y-auto">
              {organizedMenus.map(menu => {
                const role = getFormRoleForMenu(menu.id);
                return (
                  <div key={menu.id} className="form-role-section rounded-xl border border-border bg-slate-50 p-3">
                    <div className="form-role-menu-header">
                      <div className="form-role-menu-title mb-3 flex flex-wrap items-center justify-between gap-2">
                        <h4 className="text-sm font-semibold text-text">{renderMenuTitle(menu)}</h4>
                        <label className="inline-flex items-center gap-2 text-xs text-muted">
                          <input
                            type="checkbox"
                            checked={isAllMenuSelected(menu.id)}
                            ref={(input) => {
                              if (input) input.indeterminate = isSomeMenuSelected(menu.id);
                            }}
                            onChange={(e) => handleSelectAllMenu(menu.id, e.target.checked)}
                            className="rounded border-border text-primary focus:ring-primary/20"
                          />
                          Marcar todas
                        </label>
                      </div>
                      <div className="form-role-permissions flex flex-wrap gap-4 text-sm text-text">
                        <label className="inline-flex items-center gap-2">
                          <input type="checkbox" checked={role.can_view} onChange={(e) => handleFormRoleChange(menu.id, 'can_view', e.target.checked)} className="rounded border-border text-primary focus:ring-primary/20" />
                          Visualizar
                        </label>
                        <label className="inline-flex items-center gap-2">
                          <input type="checkbox" checked={role.can_create} onChange={(e) => handleFormRoleChange(menu.id, 'can_create', e.target.checked)} disabled={!role.can_view} className="rounded border-border text-primary focus:ring-primary/20" />
                          Criar
                        </label>
                        <label className="inline-flex items-center gap-2">
                          <input type="checkbox" checked={role.can_edit} onChange={(e) => handleFormRoleChange(menu.id, 'can_edit', e.target.checked)} disabled={!role.can_view} className="rounded border-border text-primary focus:ring-primary/20" />
                          Editar
                        </label>
                        <label className="inline-flex items-center gap-2">
                          <input type="checkbox" checked={role.can_delete} onChange={(e) => handleFormRoleChange(menu.id, 'can_delete', e.target.checked)} disabled={!role.can_view} className="rounded border-border text-primary focus:ring-primary/20" />
                          Deletar
                        </label>
                      </div>
                    </div>
                    {menu.children && menu.children.length > 0 && (
                      <div className="form-role-children mt-3 space-y-3 border-t border-border pt-3">
                        {menu.children.map(child => {
                          const childRole = getFormRoleForMenu(child.id);
                          return (
                            <div key={child.id} className="form-role-child-item rounded-lg border border-border bg-white p-3">
                              <div className="form-role-child-header mb-2 flex flex-wrap items-center justify-between gap-2">
                                <span className="form-role-child-label text-sm font-medium text-text">{renderMenuTitle(child)}</span>
                                <label className="inline-flex items-center gap-2 text-xs text-muted">
                                  <input
                                    type="checkbox"
                                    checked={isAllMenuSelected(child.id)}
                                    ref={(input) => {
                                      if (input) input.indeterminate = isSomeMenuSelected(child.id);
                                    }}
                                    onChange={(e) => handleSelectAllMenu(child.id, e.target.checked)}
                                    className="rounded border-border text-primary focus:ring-primary/20"
                                  />
                                  Marcar todas
                                </label>
                              </div>
                              <div className="form-role-permissions flex flex-wrap gap-4 text-sm text-text">
                                <label className="inline-flex items-center gap-2">
                                  <input type="checkbox" checked={childRole.can_view} onChange={(e) => handleFormRoleChange(child.id, 'can_view', e.target.checked)} className="rounded border-border text-primary focus:ring-primary/20" />
                                  Visualizar
                                </label>
                                <label className="inline-flex items-center gap-2">
                                  <input type="checkbox" checked={childRole.can_create} onChange={(e) => handleFormRoleChange(child.id, 'can_create', e.target.checked)} disabled={!childRole.can_view} className="rounded border-border text-primary focus:ring-primary/20" />
                                  Criar
                                </label>
                                <label className="inline-flex items-center gap-2">
                                  <input type="checkbox" checked={childRole.can_edit} onChange={(e) => handleFormRoleChange(child.id, 'can_edit', e.target.checked)} disabled={!childRole.can_view} className="rounded border-border text-primary focus:ring-primary/20" />
                                  Editar
                                </label>
                                <label className="inline-flex items-center gap-2">
                                  <input type="checkbox" checked={childRole.can_delete} onChange={(e) => handleFormRoleChange(child.id, 'can_delete', e.target.checked)} disabled={!childRole.can_view} className="rounded border-border text-primary focus:ring-primary/20" />
                                  Deletar
                                </label>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </form>
      </FormModal>

      <FormModal
        isOpen={showRolesModal && !!selectedUser}
        onClose={handleCloseRolesModal}
        title={`Permissões — ${selectedUser?.name || ''}`}
        size="xl"
        footer={(
          <>
            <button type="button" className={ui.btn} onClick={handleCloseRolesModal}>
              Cancelar
            </button>
            <button type="button" className={ui.btnPrimary} onClick={handleSaveRoles} disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar Permissões'}
            </button>
          </>
        )}
      >
        <div className="roles-content max-h-[60vh] space-y-3 overflow-y-auto">
          {organizedMenus.map(menu => {
            const role = getUserRoleForMenu(menu.id);
            return (
              <div key={menu.id} className="role-section rounded-xl border border-border bg-slate-50 p-3">
                <div className="role-menu-header">
                  <h3 className="mb-3 text-sm font-semibold text-text">{renderMenuTitle(menu)}</h3>
                  <div className="role-permissions flex flex-wrap gap-4 text-sm text-text">
                    <label className="inline-flex items-center gap-2">
                      <input type="checkbox" checked={role.can_view} onChange={(e) => handleRoleChange(menu.id, 'can_view', e.target.checked)} className="rounded border-border text-primary focus:ring-primary/20" />
                      Visualizar
                    </label>
                    <label className="inline-flex items-center gap-2">
                      <input type="checkbox" checked={role.can_create} onChange={(e) => handleRoleChange(menu.id, 'can_create', e.target.checked)} disabled={!role.can_view} className="rounded border-border text-primary focus:ring-primary/20" />
                      Criar
                    </label>
                    <label className="inline-flex items-center gap-2">
                      <input type="checkbox" checked={role.can_edit} onChange={(e) => handleRoleChange(menu.id, 'can_edit', e.target.checked)} disabled={!role.can_view} className="rounded border-border text-primary focus:ring-primary/20" />
                      Editar
                    </label>
                    <label className="inline-flex items-center gap-2">
                      <input type="checkbox" checked={role.can_delete} onChange={(e) => handleRoleChange(menu.id, 'can_delete', e.target.checked)} disabled={!role.can_view} className="rounded border-border text-primary focus:ring-primary/20" />
                      Deletar
                    </label>
                  </div>
                </div>
                {menu.children && menu.children.length > 0 && (
                  <div className="role-children mt-3 space-y-3 border-t border-border pt-3">
                    {menu.children.map(child => {
                      const childRole = getUserRoleForMenu(child.id);
                      return (
                        <div key={child.id} className="role-child-item rounded-lg border border-border bg-white p-3">
                          <span className="role-child-label mb-2 block text-sm font-medium text-text">{renderMenuTitle(child)}</span>
                          <div className="role-permissions flex flex-wrap gap-4 text-sm text-text">
                            <label className="inline-flex items-center gap-2">
                              <input type="checkbox" checked={childRole.can_view} onChange={(e) => handleRoleChange(child.id, 'can_view', e.target.checked)} className="rounded border-border text-primary focus:ring-primary/20" />
                              Visualizar
                            </label>
                            <label className="inline-flex items-center gap-2">
                              <input type="checkbox" checked={childRole.can_create} onChange={(e) => handleRoleChange(child.id, 'can_create', e.target.checked)} disabled={!childRole.can_view} className="rounded border-border text-primary focus:ring-primary/20" />
                              Criar
                            </label>
                            <label className="inline-flex items-center gap-2">
                              <input type="checkbox" checked={childRole.can_edit} onChange={(e) => handleRoleChange(child.id, 'can_edit', e.target.checked)} disabled={!childRole.can_view} className="rounded border-border text-primary focus:ring-primary/20" />
                              Editar
                            </label>
                            <label className="inline-flex items-center gap-2">
                              <input type="checkbox" checked={childRole.can_delete} onChange={(e) => handleRoleChange(child.id, 'can_delete', e.target.checked)} disabled={!childRole.can_view} className="rounded border-border text-primary focus:ring-primary/20" />
                              Deletar
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </FormModal>

      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, userId: null })}
        type="confirm"
        title="Excluir usuário"
        message="Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
      />
    </PageShell>
  );
};

export default Usuarios;

